import type { ProductColorOption } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useStore } from 'zustand';
import { assembleEditAreaInput, useEditAreaDraftStore } from '@/entities/edit-area';
import { describeEditAreaIssue } from '../model/describe-edit-area-issue';

interface Props {
  readonly colors?: readonly Pick<ProductColorOption, 'id' | 'name'>[];
}

/**
 * 사진 위에서 이미 빨갛게 보이는 문제라 목록에 다시 적지 않는다(저장을 누르면 저장 동작이 사유를 보인다).
 * 계약 collectFaceIssues 의 윤곽 위반 문구와 같아야 한다.
 */
const SHOWN_ON_PHOTO = new Set<string>(['배치가 제품 윤곽을 벗어났습니다.']);

const focusSection = (targetId: string): void => {
  const target = document.getElementById(targetId);
  if (target === null) return;
  // 대상이 접힌 <details> 자체이거나 그 안에 있으면(합성 에셋 줄은 '상품 목업 이미지 관리' 안) 전부 열어야 보인다.
  for (
    let details = target.closest('details');
    details !== null;
    details = details.parentElement?.closest('details') ?? null
  )
    details.open = true;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  target.focus({ preventScroll: true });
};

/** 저장 전에 고칠 문제·명령 거부·안내가 있을 때만 보인다. 문제가 없으면 아무것도 그리지 않는다. */
export const EditAreaValidationSummary = ({ colors = [] }: Props): ReactElement | null => {
  const state = useStore(useEditAreaDraftStore());
  const result = assembleEditAreaInput(state);
  const descriptions = result.success
    ? []
    : [
        ...new Map(
          result.issues
            .filter((issue) => !SHOWN_ON_PHOTO.has(issue.message))
            .map((issue) => {
              const description = describeEditAreaIssue(issue, state, colors);
              return [description.key, description] as const;
            }),
        ).values(),
      ];
  if (descriptions.length === 0 && !state.commandError && !state.notice) return null;
  return (
    <section aria-label="편집영역 검증" className="rounded border p-4 text-sm">
      <h2 className="font-semibold">저장 전 확인</h2>
      {state.commandError ? (
        <p role="alert" className="text-destructive">
          {state.commandError}
        </p>
      ) : null}
      {state.notice ? <p role="status">{state.notice}</p> : null}
      {descriptions.length === 0 ? null : (
        <ul className="list-inside list-disc">
          {descriptions.map((issue) => (
            <li key={issue.key}>
              <button
                type="button"
                className="text-left underline underline-offset-2"
                onClick={() => focusSection(issue.targetId)}
              >
                {issue.label}
              </button>
              : {issue.message}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
